import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronRight, ArrowRight, Play, Pause, Settings,
    BarChart3, Sliders, Maximize2, MoreHorizontal,
    Cpu, ShieldCheck, Zap, Globe, Database, Scale,
    Microscope, Timer, Box, FileText, HelpCircle,
    Atom, ChevronDown, Laptop, Layers, Share2, Download,
    Target, Sparkles, Plus, Minus, Activity, Code, Terminal,
    GitBranch, Server, BookOpen, FlaskConical, MousePointer2,
    CheckCircle2, Lock, Clock, GraduationCap, X
} from 'lucide-react';
import { gsap, ScrollTrigger } from '../hooks/useGSAP';
import TextMarquee from '../components/common/TextMarquee';

import HorizontalScroll, { HorizontalScrollItem } from '../components/common/HorizontalScroll';
import Footer from '../components/common/Footer';

// Assets
import featureBeam from '../assets/feature_beam.png';
import featureDetector from '../assets/feature_detector.png';
import featureAnalysis from '../assets/feature_analysis.png';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// ============================================
// GLOBAL NOISE OVERLAY
// ============================================


// ============================================
// ZOOM HERO - MONOCHROME
// ============================================
const ZoomHero = () => {
    const sectionRef = useRef(null);
    const contentRef = useRef(null);
    const titleRef = useRef(null);
    const line1Ref = useRef(null);
    const line2Ref = useRef(null);

    useIsomorphicLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ delay: 0.2 });

            // Entrances
            const chars1 = line1Ref.current?.querySelectorAll('.char');
            const chars2 = line2Ref.current?.querySelectorAll('.char');

            if (chars1) {
                tl.fromTo(chars1,
                    { opacity: 0, y: 150, rotateX: -90, scale: 0.5 },
                    { opacity: 1, y: 0, rotateX: 0, scale: 1, duration: 1.2, stagger: 0.04, ease: 'power4.out' },
                    '-=0.4'
                );
            }
            if (chars2) {
                tl.fromTo(chars2,
                    { opacity: 0, y: 150, rotateX: -90, scale: 0.5 },
                    { opacity: 1, y: 0, rotateX: 0, scale: 1, duration: 1.2, stagger: 0.04, ease: 'power4.out' },
                    '-=1.0'
                );
            }

            tl.fromTo('.hero-fade', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1, stagger: 0.2 }, '-=0.5');

            // ZOOM SCROLL EFFECT
            gsap.to(contentRef.current, {
                scale: 0.85,
                opacity: 0,
                y: -100,
                filter: 'blur(10px)',
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top top',
                    end: 'bottom center',
                    scrub: true
                }
            });

        }, sectionRef);
        return () => ctx.revert();
    }, []);

    const splitChars = (text, className = '') => text.split('').map((char, i) => (
        <span key={i} className={`char inline-block ${className}`} style={{ transformOrigin: 'center bottom' }}>{char === ' ' ? '\u00A0' : char}</span>
    ));

    return (
        <section ref={sectionRef} className="relative h-screen sticky top-0 flex flex-col items-center justify-start px-6 pt-48 bg-[#f5f5f7] overflow-hidden perspective-1000 z-0 border-b border-gray-200">
            {/* Monochrome Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-20%] left-[-20%] w-[60%] h-[80%] bg-gray-200/40 rounded-full blur-[150px] mix-blend-multiply opacity-50" />
                <div className="absolute bottom-[-20%] right-[-20%] w-[60%] h-[80%] bg-gray-300/40 rounded-full blur-[150px] mix-blend-multiply opacity-50" />
            </div>

            <div ref={contentRef} className="relative z-10 max-w-[100rem] mx-auto w-full origin-center">
                {/* Stats Bar */}
                <div className="hero-fade flex flex-wrap justify-center gap-4 md:gap-12 mb-16 text-xs md:text-sm font-mono text-[#86868b] uppercase tracking-wider">
                    <div className="flex items-center gap-2 border border-gray-200 px-3 py-1 rounded-full bg-white/50 backdrop-blur-sm">
                        <GraduationCap size={14} />
                        Free & Open Source
                    </div>
                    <div className="flex items-center gap-2 border border-gray-200 px-3 py-1 rounded-full bg-white/50 backdrop-blur-sm">
                        <FlaskConical size={14} />
                        50+ Experiments
                    </div>
                    <div className="flex items-center gap-2 border border-gray-200 px-3 py-1 rounded-full bg-white/50 backdrop-blur-sm">
                        <Globe size={14} />
                        16 Global Facilities
                    </div>
                </div>

                {/* Main Heading */}
                <div ref={titleRef} className="text-center mb-16 perspective-1200">
                    <h1 ref={line1Ref} className="font-display text-[5rem] md:text-[8rem] lg:text-[10rem] leading-[0.85] font-medium tracking-tighter text-[#1d1d1f] mb-2 filter contrast-125">
                        {splitChars('PARTICLE')}
                    </h1>
                    <h1 ref={line2Ref} className="font-display text-[5rem] md:text-[8rem] lg:text-[10rem] leading-[0.85] font-medium tracking-tighter text-[#86868b]">
                        {splitChars('Simulator')}
                    </h1>
                </div>

                <p className="hero-fade text-center text-xl md:text-3xl text-[#424245] max-w-4xl mx-auto leading-relaxed font-regular mb-10">
                    The GeoGebra of particle physics: explore real experiments from CERN, DESY, Fermilab & more. Learn by doing.
                </p>

                {/* MVP Explanation */}
                <div className="hero-fade max-w-3xl mx-auto mb-16 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
                        <div className="w-10 h-10 bg-[#1d1d1f] rounded-xl flex items-center justify-center mx-auto mb-3">
                            <Atom size={20} className="text-white" />
                        </div>
                        <h4 className="font-bold text-[#1d1d1f] mb-1">Real Physics</h4>
                        <p className="text-xs text-gray-500">Highland formula, relativistic kinematics, actual detector responses</p>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
                        <div className="w-10 h-10 bg-[#1d1d1f] rounded-xl flex items-center justify-center mx-auto mb-3">
                            <BookOpen size={20} className="text-white" />
                        </div>
                        <h4 className="font-bold text-[#1d1d1f] mb-1">Learn by Doing</h4>
                        <p className="text-xs text-gray-500">Interactive 3D experiments with guided learning objectives</p>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
                        <div className="w-10 h-10 bg-[#1d1d1f] rounded-xl flex items-center justify-center mx-auto mb-3">
                            <Target size={20} className="text-white" />
                        </div>
                        <h4 className="font-bold text-[#1d1d1f] mb-1">For Everyone</h4>
                        <p className="text-xs text-gray-500">From curious kids to physics students, with difficulty levels for all</p>
                    </div>
                </div>


            </div>
        </section>
    );
};

// ============================================
// PINNED PIPELINE - TECH SPECS
// ============================================
const PinnedPipeline = () => {
    const sectionRef = useRef(null);
    const stepsRef = useRef([]);

    useIsomorphicLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top top',
                    end: '+=250%',
                    pin: true,
                    scrub: 1
                }
            });

            // Draw line
            tl.fromTo('.pipeline-draw-path',
                { strokeDasharray: 2000, strokeDashoffset: 2000 },
                { strokeDashoffset: 0, ease: 'none', duration: 1 }
            );

            // Activate Steps
            stepsRef.current.forEach((step, i) => {
                const progressPoint = (i + 1) * 0.22;
                tl.to(step.querySelector('.step-active'), { opacity: 1, scale: 1, borderColor: '#1d1d1f', duration: 0.1 }, progressPoint);
                tl.to(step.querySelector('.step-icon'), { color: '#1d1d1f', scale: 1.2, duration: 0.1 }, progressPoint);
                tl.to(step.querySelector('.step-num'), { backgroundColor: '#1d1d1f', color: '#ffffff', duration: 0.1 }, progressPoint);
            });

        }, sectionRef);
        return () => ctx.revert();
    }, []);

    const steps = [
        { icon: MousePointer2, title: "Pick Experiment", desc: "50+ Ready Setups", specs: ["CERN, DESY, Fermilab", "Beginner to Advanced", "Guided Objectives"] },
        { icon: Sliders, title: "Adjust Physics", desc: "Real Parameters", specs: ["Particle Type & Energy", "Material Properties", "Detector Config"] },
        { icon: Play, title: "Run & Visualize", desc: "Interactive 3D", specs: ["Live Particle Tracks", "Detector Response", "Physics Overlays"] },
        { icon: GraduationCap, title: "Learn & Export", desc: "Understand Results", specs: ["MCS, TOF, Decay", "Data Export CSV", "Compare Theory"] },
    ];

    return (
        <section ref={sectionRef} className="h-screen bg-white relative overflow-hidden z-20 flex flex-col justify-center border-b border-gray-200">
            <div className="max-w-7xl mx-auto w-full px-6">
                <div className="text-center mb-24">
                    <span className="text-black font-bold text-xs uppercase tracking-widest mb-4 block border border-black/10 inline-block px-3 py-1 rounded-full">How It Works</span>
                    <h2 className="text-5xl md:text-7xl font-semibold text-[#1d1d1f] tracking-tight">Learn Physics by Doing</h2>
                </div>

                <div className="relative">
                    {/* Base Line */}
                    <svg className="absolute top-16 left-0 w-full h-32 hidden md:block overflow-visible z-0 pointer-events-none" preserveAspectRatio="none">
                        <path d="M 50 50 L 1150 50" stroke="#f0f0f0" strokeWidth="4" fill="none" />
                        <path className="pipeline-draw-path" d="M 50 50 L 1150 50" stroke="#000000" strokeWidth="4" fill="none" />
                    </svg>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {steps.map((step, i) => (
                            <div key={i} ref={el => stepsRef.current[i] = el} className="relative z-10 flex flex-col items-center text-center group">
                                <div className="w-32 h-32 bg-white border border-gray-100 rounded-full flex items-center justify-center mb-8 shadow-lg relative group-hover:shadow-2xl transition-all duration-500">
                                    <step.icon size={40} className="step-icon text-gray-300 transition-all duration-300" />
                                    <div className="step-active absolute inset-0 rounded-full border-2 border-black opacity-0 scale-90 transition-all duration-300" />
                                </div>
                                <div className="step-num bg-[#f5f5f7] px-4 py-1.5 rounded-full text-xs font-mono font-bold text-[#86868b] mb-4 transition-colors duration-300">STEP 0{i + 1}</div>
                                <h3 className="text-2xl font-bold text-[#1d1d1f] mb-2">{step.title}</h3>
                                <p className="text-sm text-[#424245] font-medium mb-3">{step.desc}</p>

                                {/* Tech Specs */}
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-mono space-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    {step.specs.map((s, j) => <div key={j} className="border-t border-gray-100 pt-1 w-full">{s}</div>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// HORIZONTAL BLUEPRINTS - MONOCHROME
// ============================================
const HorizontalBlueprints = () => {
    return (
        <div className="bg-black text-white overflow-hidden z-30 relative border-b border-white/10">
            <HorizontalScroll className="h-screen" speed={1} showProgress={true}>
                {/* INTRO PANEL */}
                <HorizontalScrollItem className="w-screen h-screen flex items-center justify-center p-20 border-r border-white/10">
                    <div className="max-w-4xl text-center">
                        <span className="text-white/50 font-mono text-xs mb-6 block border border-white/20 inline-block px-2 py-1 rounded">EXPLORE_PHYSICS</span>
                        <h2 className="text-[8rem] font-semibold tracking-tighter leading-[0.9] mb-8 text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-600">What<br />You'll Learn.</h2>
                        <p className="text-2xl text-gray-500 max-w-xl mx-auto">
                            Scroll right to discover the physics concepts you can explore.
                        </p>
                        <ArrowRight className="mx-auto mt-12 animate-bounce text-white" size={40} />
                    </div>
                </HorizontalScrollItem>

                {/* PARTICLE PHYSICS PANEL */}
                <HorizontalScrollItem className="w-screen h-screen p-20 flex items-center border-r border-white/10 bg-black">
                    <div className="grid grid-cols-2 gap-24 w-full max-w-7xl mx-auto items-center">
                        <div className="space-y-12">
                            <div className="w-20 h-20 border border-white/30 rounded-2xl flex items-center justify-center bg-white/5">
                                <Atom size={40} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-6xl font-medium mb-6 text-white">Particle Interactions</h3>
                                <p className="text-xl text-gray-400 leading-relaxed font-light">
                                    Experience how particles scatter, decay, and interact with matter.
                                    From Rutherford scattering to cosmic ray showers; see it all in 3D.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-6 font-mono text-sm text-gray-400">
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Coulomb Scattering</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Particle Decay</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Cherenkov Radiation</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Bragg Peak</div>
                            </div>
                        </div>
                        <div className="h-full min-h-[500px] bg-white/5 rounded-3xl border border-white/10 relative overflow-hidden flex items-center justify-center">
                            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '30px 30px', opacity: 0.1 }} />
                            <div className="text-center">
                                <div className="text-[100px] font-bold text-white/5">p → μ → e</div>
                                <div className="font-mono text-white/30 mt-4">DECAY_CHAIN_VIS</div>
                            </div>
                        </div>
                    </div>
                </HorizontalScrollItem>

                {/* DETECTORS PANEL */}
                <HorizontalScrollItem className="w-screen h-screen p-20 flex items-center border-r border-white/10 bg-black">
                    <div className="grid grid-cols-2 gap-24 w-full max-w-7xl mx-auto items-center">
                        <div className="h-full min-h-[500px] bg-white/5 rounded-3xl border border-white/10 relative overflow-hidden flex items-center justify-center">
                            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '30px 30px', opacity: 0.1 }} />
                            <div className="grid grid-cols-3 gap-4 rotate-12 scale-110 opacity-30">
                                {[...Array(9)].map((_, i) => (
                                    <div key={i} className="w-24 h-24 border border-white/40 rounded-lg animate-pulse bg-white/5" style={{ animationDelay: `${i * 0.1}s` }} />
                                ))}
                            </div>
                        </div>
                        <div className="space-y-12">
                            <div className="w-20 h-20 border border-white/30 rounded-2xl flex items-center justify-center bg-white/5">
                                <Layers size={40} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-6xl font-medium mb-6 text-white">Real Detectors</h3>
                                <p className="text-xl text-gray-400 leading-relaxed font-light">
                                    Visualize how real particle detectors work: silicon trackers, TOF counters,
                                    Cherenkov detectors, calorimeters, and magnetic spectrometers.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-6 font-mono text-sm text-gray-400">
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Silicon Trackers</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Time-of-Flight</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Calorimeters</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Dipole Magnets</div>
                            </div>
                        </div>
                    </div>
                </HorizontalScrollItem>

                {/* FACILITIES PANEL */}
                <HorizontalScrollItem className="w-screen h-screen p-20 flex items-center bg-black">
                    <div className="grid grid-cols-2 gap-24 w-full max-w-7xl mx-auto items-center">
                        <div className="space-y-12">
                            <div className="w-20 h-20 border border-white/30 rounded-2xl flex items-center justify-center bg-white/5">
                                <Globe size={40} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-6xl font-medium mb-6 text-white">Global Facilities</h3>
                                <p className="text-xl text-gray-400 leading-relaxed font-light">
                                    Pre-configured experiments from the world's top physics labs.
                                    CERN T9/T10, DESY Test Beam, Fermilab, J-PARC, and more.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-6 font-mono text-sm text-gray-400">
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">CERN (Geneva)</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">DESY (Hamburg)</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">Fermilab (Chicago)</div>
                                <div className="border hover:bg-white/10 border-white/20 p-4 rounded-lg transition-colors">J-PARC (Japan)</div>
                            </div>
                        </div>
                        <div className="h-full min-h-[500px] bg-white/5 rounded-3xl border border-white/10 relative overflow-hidden flex items-center justify-center">
                            <div className="text-center space-y-4">
                                <div className="text-[80px] font-bold text-white/10 opacity-20">
                                    <Globe size={100} strokeWidth={1} />
                                </div>
                                <div className="font-mono text-white/30">16 FACILITIES • 50+ EXPERIMENTS</div>
                                <div className="text-sm text-white/20">From beginner cloud chambers to advanced spectrometers</div>
                            </div>
                        </div>
                    </div>
                </HorizontalScrollItem>
            </HorizontalScroll>
        </div>
    );
};

// ============================================
// HOLOGRAPHIC PRO LAB - MONOCHROME
// ============================================
const HolographicLab = () => {
    const containerRef = useRef(null);
    const cardRef = useRef(null);
    const [momentum, setMomentum] = useState(0.7);
    const [material, setMaterial] = useState('PVC');

    // Material radiation lengths in cm
    const X0 = material === 'PVC' ? 15.8 : 35.0; // PVC vs Delrin
    const thickness = 2.0; // cm
    const mass = 0.938; // proton mass GeV
    const gamma = Math.sqrt(Math.pow(momentum, 2) + Math.pow(mass, 2)) / mass;
    const beta = Math.sqrt(1 - 1 / (gamma * gamma));
    const thetaRMS = (13.6e-3 / (beta * momentum)) * Math.sqrt(thickness / X0) * (1 + 0.038 * Math.log(thickness / X0));

    const handleMouseMove = (e) => {
        if (!containerRef.current || !cardRef.current) return;
        const { left, top, width, height } = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - left) / width;
        const y = (e.clientY - top) / height;

        gsap.to(cardRef.current, {
            rotateY: (x - 0.5) * 10,
            rotateX: (y - 0.5) * -10,
            duration: 0.5
        });
    };

    const handleMouseLeave = () => {
        gsap.to(cardRef.current, { rotateY: 0, rotateX: 0, duration: 0.5 });
    };

    return (
        <section className="py-40 px-6 bg-[#f5f5f7] perspective-1000 overflow-hidden border-b border-gray-200" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div ref={containerRef} className="max-w-7xl mx-auto relative z-10 w-full">
                <div className="text-center mb-24">
                    <span className="text-gray-400 font-bold text-xs uppercase tracking-widest mb-4 block border border-gray-200 inline-block px-3 py-1 rounded-full bg-white">Try It Now</span>
                    <h2 className="text-5xl font-semibold text-[#1d1d1f]">See Physics in Action</h2>
                </div>

                {/* 3D CARD */}
                <div ref={cardRef} className="bg-white rounded-[3rem] p-12 md:p-16 shadow-2xl shadow-black/10 border border-gray-200" style={{ transformStyle: 'preserve-3d' }}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" style={{ transform: 'translateZ(20px)' }}>
                        <div className="lg:col-span-1 space-y-10">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 block">Target Material</label>
                                <div className="flex bg-[#f5f5f7] p-1.5 rounded-xl">
                                    {['PVC', 'Delrin'].map(m => (
                                        <button
                                            key={m} onClick={() => setMaterial(m)}
                                            className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all ${material === m ? 'bg-black shadow-md text-white' : 'text-gray-400 hover:text-black'}`}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 block">Proton Momentum (p)</label>
                                <div className="bg-[#f5f5f7] p-8 rounded-3xl">
                                    <div className="flex justify-between mb-6">
                                        <span className="text-3xl font-bold">{momentum.toFixed(2)}</span>
                                        <span className="text-gray-400 font-medium">GeV/c</span>
                                    </div>
                                    <input
                                        type="range" min="0.3" max="1.5" step="0.05"
                                        value={momentum} onChange={(e) => setMomentum(parseFloat(e.target.value))}
                                        className="w-full h-3 bg-gray-300 rounded-full appearance-none cursor-pointer accent-black"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-[#1d1d1f] text-white p-10 rounded-3xl flex flex-col justify-between shadow-lg transform transition-transform hover:scale-105">
                                <div className="flex items-center gap-3 text-white/50 mb-4">
                                    <Activity size={20} />
                                    <span className="text-xs font-mono uppercase tracking-widest">Scattering Angle θ_RMS</span>
                                </div>
                                <div className="text-5xl font-mono font-medium tracking-tighter">{(thetaRMS * 1000).toFixed(2)} mrad</div>
                            </div>

                            <div className="bg-white border-2 border-[#f5f5f7] p-10 rounded-3xl flex flex-col justify-between transform transition-transform hover:scale-105">
                                <div className="flex items-center gap-3 text-gray-400 mb-4">
                                    <Zap size={20} />
                                    <span className="text-xs font-mono uppercase tracking-widest">Radiation Length X₀</span>
                                </div>
                                <div className="text-7xl font-mono font-medium text-[#1d1d1f] tracking-tighter">{X0.toFixed(1)} cm</div>
                            </div>

                            <div className="md:col-span-2 bg-black rounded-2xl p-6 font-mono text-xs text-gray-300 shadow-inner overflow-hidden relative min-h-[120px]">
                                <div className="opacity-70 space-y-2">
                                    <div className="text-gray-500">// MCS KERNEL LOG</div>
                                    <div>{`> MATERIAL: ${material} X0=${X0}cm`}</div>
                                    <div>{`> PROTON: p=${momentum}GeV/c β=${beta.toFixed(4)}`}</div>
                                    <div>{`> θ_RMS = ${(thetaRMS * 1000).toFixed(3)} mrad`}</div>
                                    <div className="text-white/80 animate-pulse">{`> HIGHLAND FORMULA ACTIVE`}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// RICH CURRICULUM MODULES
// ============================================
const RichCurriculum = () => {
    return (
        <section className="py-40 bg-white px-6">
            <div className="max-w-5xl mx-auto">
                <div className="mb-24 text-center">
                    <span className="text-black font-bold text-xs uppercase tracking-widest mb-4 block border border-black/10 inline-block px-3 py-1 rounded-full">Explore</span>
                    <h2 className="text-5xl font-semibold text-[#1d1d1f]">Experiment Categories</h2>
                </div>

                <div className="space-y-6">
                    {[
                        { title: "Fundamentals & Relativity", desc: "E=mc², time dilation, momentum-energy", tags: ["Beginner", "Theory"], duration: "10+ experiments", link: "/simulator" },
                        { title: "Particle Interactions", desc: "Scattering, energy loss, Cherenkov", tags: ["Intermediate", "MCS"], duration: "12+ experiments", link: "/simulator" },
                        { title: "Decay Physics", desc: "Pion, muon, kaon lifetimes", tags: ["Intermediate", "TOF"], duration: "8+ experiments", link: "/simulator" },
                        { title: "Detector Technology", desc: "TOF, calorimeters, tracking systems", tags: ["Advanced", "Hardware"], duration: "15+ experiments", link: "/simulator" },
                        { title: "Medical & Applied Physics", desc: "Proton therapy, Bragg curves, imaging", tags: ["Advanced", "Medical"], duration: "6+ experiments", link: "/simulator" }
                    ].map((mod, i) => (
                        <Link key={i} to={mod.link} className="block">
                            <div className="sticky top-20 bg-white border border-gray-200 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all flex flex-col md:flex-row justify-between items-center group gap-6 cursor-pointer hover:-translate-y-0.5">
                                <div className="flex items-center gap-6 w-full">
                                    <div className="w-16 h-16 bg-[#f5f5f7] rounded-xl flex items-center justify-center text-gray-500 font-bold text-lg group-hover:bg-black group-hover:text-white transition-colors flex-shrink-0">
                                        0{i + 1}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <h3 className="text-2xl font-bold text-[#1d1d1f]">{mod.title}</h3>
                                            {mod.tags.map(tag => (
                                                <span key={tag} className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-gray-500">{mod.desc}</p>

                                        {/* Progress Bar Mockup */}
                                        <div className="w-full h-1 bg-gray-100 mt-4 rounded-full overflow-hidden">
                                            <div className="h-full bg-black w-0 group-hover:w-[30%] transition-all duration-1000" />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 flex-shrink-0 w-full md:w-auto justify-between md:justify-end">
                                    <div className="flex flex-col items-end text-xs text-gray-400 font-mono">
                                        <span className="flex items-center gap-1"><Clock size={12} /> {mod.duration}</span>
                                        <span className="flex items-center gap-1"><GraduationCap size={12} /> Beginner</span>
                                    </div>
                                    <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center group-hover:bg-black group-hover:border-black transition-colors">
                                        <ArrowRight className="text-gray-300 group-hover:text-white scale-75" />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// MEGA FOOTER
// ============================================
const MegaFooter = () => (
    <footer className="bg-[#111111] text-white py-24 border-t border-[#333]">
        <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24">
                {/* Brand Column */}
                <div className="col-span-1 md:col-span-1 space-y-8">
                    <Link to="/" className="block w-fit">
                        <h5 className="font-bold text-2xl tracking-tighter text-white hover:opacity-80 transition-opacity">Particle Simulator</h5>
                    </Link>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        An interactive particle physics learning platform. Like GeoGebra for physics: explore real experiments from world-class facilities.
                    </p>
                    <div className="flex items-center gap-2 text-xs font-mono text-white border border-white/20 bg-white/5 px-3 py-1.5 rounded-full w-fit">
                        <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        FREE & OPEN SOURCE
                    </div>
                </div>

                {/* Platform Column */}
                <div>
                    <h6 className="font-bold text-sm uppercase tracking-widest text-gray-500 mb-6">Platform</h6>
                    <ul className="space-y-4 text-sm text-gray-300">
                        <li><Link to="/simulator" className="hover:text-white transition-colors">Launch Simulator</Link></li>
                        <li><Link to="/learn" className="hover:text-white transition-colors">Learn</Link></li>
                        <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-[#333] pt-12 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500">
                <p>&copy; 2026 Particle Simulator. Open source educational project.</p>
            </div>
        </div>
    </footer>
);

// ============================================
// MAIN HOME
// ============================================
const Home = () => {
    return (
        <div className="bg-[#f5f5f7] min-h-screen font-sans selection:bg-black selection:text-white overflow-x-hidden">

            <ZoomHero />
            <PinnedPipeline />
            <HorizontalBlueprints />
            <HolographicLab />
            <RichCurriculum />
            <MegaFooter />
        </div>
    );
};

export default Home;
